import React, { useState } from 'react';
import { generatePath, Link, useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  Box,
  Breadcrumbs,
  Button,
  FormControl,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import { Home as HomeIcon } from '@mui/icons-material';
import {
  StyledRescuerBreadcrumb,
  StyledRescuerTextBreadcrumb,
} from '../../rescuerDashboard/rescuerDashboardMobile/Components/StyledRescuerBreadcrumb';
import routes from '../../../routes';
import { Colors } from '../../../assets/theme/Colors';
import { updateUserCourseModule } from '../../../actions/users';
import { QUIZ_MODULE_NAME } from '../LearningCenterMobile/LearningCenterMobileView';

const LearningCenterMobilQuizBreadcrumbs = () => (
  <Box boxShadow={0}>
    <Breadcrumbs aria-label="breadcrumb">
      <StyledRescuerBreadcrumb
        component={Link}
        to={generatePath(routes.mobileDashboard)}
        label="Home"
        icon={<HomeIcon style={{ color: '#fff' }} fontSize="small" />}
      />
      <StyledRescuerTextBreadcrumb component={Link} to={generatePath(routes.mobileDashboardLearn)} label="Learn" />
      <StyledRescuerTextBreadcrumb component="a" href="#" label="Quiz" />
    </Breadcrumbs>
  </Box>
);

const QuizModule = ({ userId, course, setCurrentCoursesState, isMobile = false }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const [selectedValues, setSelectedValues] = useState({});
  const { id, quiz, title, modules } = course;
  const module = modules.find((m) => m.name === QUIZ_MODULE_NAME);

  const handleChange = (questionId, event) => {
    setSelectedValues((prev) => ({
      ...prev,
      [questionId]: event?.target?.value,
    }));
  };
  const handleDone = () => {
    setCurrentCoursesState(id);
    if (!module.completed) {
      dispatch(updateUserCourseModule(userId, id, module.id));
    }

    if (isMobile) {
      return history.push(generatePath(routes.mobileDashboardLearn));
    }
    return history.push(generatePath(routes.learn));
  };

  const renderRadioStyles = (isSelected) => {
    if (isSelected) {
      return {
        color: 'white',
      };
    }

    return null; // Style only changes when selected
  };

  const renderLabelStyles = (isSelected, isCorrect) => {
    if (isSelected) {
      if (isCorrect) {
        return {
          backgroundColor: Colors.green.light,
          borderRadius: 5,
          color: 'white',
        };
      }

      return {
        backgroundColor: Colors.red,
        borderRadius: 5,
        color: 'white',
        fontWeight: '700',
      };
    }

    return null;
  };

  // Check if all selected answers are correct
  const allCorrect = (quiz || []).every((q) => {
    const correctAnswer = q.answers.find((answer) => answer.correct);
    // Check if selected answer matches correct one
    return selectedValues[q.id] === correctAnswer.value;
  });

  if (!id) {
    if (isMobile) {
      return history.push(generatePath(routes.mobileDashboardLearn));
    }
    return history.push(generatePath(routes.learn));
  }

  return (
    <>
      {isMobile && <LearningCenterMobilQuizBreadcrumbs />}

      <Box sx={{ height: '100%', width: '100%', overflow: 'auto', backgroundColor: 'background.default', padding: 1 }}>
        <Typography variant="h6">{title} Quiz</Typography>

        <Paper sx={{ padding: 2, marginTop: 1 }}>
          <Box>
            <Typography variant="body1">
              Select the correct answer from the options provided. This quiz is just a learning aid, you can attempt
              this quiz as many times as you want!
            </Typography>
          </Box>

          <Box my={2}>
            {(quiz || []).map((q) => {
              const selectedAnswerValue = selectedValues[q.id];
              const selectedAnswer = q.answers.find((answer) => answer.value === selectedAnswerValue);

              return (
                <FormControl variant="standard" key={q.id}>
                  <Typography style={{ fontWeight: 500 }} variant="body1">
                    {q.question}
                  </Typography>

                  {selectedAnswer && !selectedAnswer?.correct && (
                    <Typography variant="body2" sx={{ color: Colors.errorText }}>
                      Uh oh! This is incorrect, please try a different answer.
                    </Typography>
                  )}

                  <RadioGroup row value={selectedValues[q.id] || ''} onChange={(event) => handleChange(q.id, event)}>
                    {(q?.answers || []).map((answer) => {
                      const isCorrect = answer.correct;
                      const isSelected = answer.value === selectedAnswerValue;

                      return (
                        <FormControlLabel
                          key={answer.value}
                          value={answer.value}
                          control={<Radio style={renderRadioStyles(isSelected)} />}
                          label={answer.label}
                          sx={{ marginBottom: 1, paddingRight: 2, marginLeft: 0 }}
                          style={renderLabelStyles(isSelected, isCorrect)}
                        />
                      );
                    })}
                  </RadioGroup>
                </FormControl>
              );
            })}
          </Box>
        </Paper>

        {allCorrect && (
          <Box py={2} display="flex" justifyContent="end">
            <Button variant="contained" color="primary" onClick={handleDone}>
              Done
            </Button>
          </Box>
        )}
      </Box>
    </>
  );
};

export default QuizModule;
